/** @jsxImportSource @emotion/react */
import { useState } from 'react'
import { Box, Form, Text } from 'grommet'
import { useTranslation } from 'react-i18next'
import { ApiError } from '@supabase/supabase-js'

import { routes } from 'routes'
import { trackEvent } from 'utils/useGTM'
import { supabaseClient } from 'supabaseClient'
import FormBody from 'components/form/FormBody'
import Notification from 'components/Notification'
import FormFooter from 'components/form/FormFooter'
import FormSection from 'components/form/FormSection'
import LoadingContainer from 'components/LoadingContainer'
import EmailField from 'components/form/fields/EmailField'
import SubmitFormButton from 'components/form/SubmitFormButton'

import BackToLogin from '../BackToLogin'

type ForgotPasswordForm = {
  email: string
}

export default function ForgotPasswordForm() {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<ApiError | undefined>()
  const [done, setDone] = useState(false)

  const [form, setForm] = useState<ForgotPasswordForm>({
    email: '',
  })

  return (
    <LoadingContainer loading={loading} title={t('pages.reset-password.title')}>
      <Form<ForgotPasswordForm>
        className="forgot-password-form"
        messages={{ required: t('validation.required'), invalid: t('validation.invalid') }}
        value={form}
        validate="change"
        onChange={(nextValue) => setForm(nextValue)}
        onSubmit={async ({ value }) => {
          setLoading(true)
          const { error } = await supabaseClient.auth.api.resetPasswordForEmail(value.email, {
            redirectTo: `${routes.domain}${routes.resetPassword}`,
          })
          setLoading(false)
          if (error) {
            trackEvent({ event: 'ErrorForgotPassword' })
            setError(error)
          } else {
            trackEvent({ event: 'ForgotPassword' })
            setError(undefined)
            setDone(true)
          }
        }}>
        {!done && (
          <FormBody gap="medium">
            <Box pad={{ horizontal: 'large' }}>
              <Text textAlign="center" size="small">
                {t('pages.forgot-password.description')}
              </Text>
            </Box>
            <EmailField disabled={loading} />
            {error && (
              <FormSection>
                <Notification status="error">{error?.message}</Notification>
              </FormSection>
            )}
            <FormFooter margin={{ vertical: 'small' }}>
              <SubmitFormButton size="medium" fetching={loading} label={t('cta.submit')} />
            </FormFooter>
          </FormBody>
        )}
        {done && (
          <Box gap="medium">
            <FormBody pad={{ horizontal: 'large' }}>
              <Notification status="ok">{t('pages.forgot-password.link-sent')}</Notification>
              <Box margin={{ top: 'medium' }} pad={{ horizontal: 'medium' }}>
                <Text size="small" textAlign="center">
                  {t('pages.forgot-password.details1')}
                </Text>
                <br />
                <Text size="small" textAlign="center">
                  {t('pages.forgot-password.details2')}
                </Text>
              </Box>
              <BackToLogin />
            </FormBody>
          </Box>
        )}
      </Form>
    </LoadingContainer>
  )
}
